import React, { useState } from 'react';
import { Box, Typography, Alert, Button } from '@mui/material';
import { Refresh as RetryIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { hasAgentAccess, AGENT_ROLES, getAgentName } from '../lib/agentRoles';
import AgentAccessDenied from '../components/Agents/AgentAccessDenied';
import LoglineAnalysisForm from '../components/Agents/LoglineAnalysisForm';
import AnalysisLoadingScreen from '../components/Agents/AnalysisLoadingScreen';
import LoglineAnalysisResults from '../components/Agents/LoglineAnalysisResults';
import { analyzeLogline } from '../lib/agentsApi';
import { brandColors } from '../theme/theme';

/**
 * Logline Analysis Agent Page
 * 
 * AI-powered story analysis tool that provides comprehensive film concept evaluation.
 * Requires logline_analysis_agent role for access.
 */
const AgentsLoglineAnalysis = () => {
  const { userProfile } = useAuth();
  const agentName = getAgentName(AGENT_ROLES.LOGLINE_ANALYSIS);
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [lastSubmission, setLastSubmission] = useState(null);

  // Check if user has access to this specific agent
  const hasAccess = hasAgentAccess(userProfile, AGENT_ROLES.LOGLINE_ANALYSIS);

  if (!hasAccess) {
    return <AgentAccessDenied agentName={agentName} />;
  }

  // Handle form submission with real API call
  const handleFormSubmit = async (data) => {
    console.log('📝 Form submitted:', data);
    
    setIsLoading(true);
    setError(null);
    setLastSubmission(data);
    
    try {
      // Call the real API
      const result = await analyzeLogline(data);
      
      console.log('✅ Analysis complete:', result);
      
      // Check if successful
      if (result.success && result.data) {
        setAnalysisResult(result.data);
      } else {
        throw new Error('Analysis completed but no data returned');
      }
    } catch (err) {
      console.error('❌ Analysis failed:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle retry
  const handleRetry = () => {
    if (lastSubmission) {
      handleFormSubmit(lastSubmission);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setIsLoading(false);
    setError('Analysis cancelled by user');
  };

  // Handle new analysis
  const handleNewAnalysis = () => {
    setAnalysisResult(null);
    setError(null);
    setLastSubmission(null);
  };

  // Show loading screen during analysis
  if (isLoading) {
    return <AnalysisLoadingScreen onCancel={handleCancel} />;
  }

  // Show results with professional display
  if (analysisResult) {
    return (
      <Box sx={{ p: 3 }}>
        <LoglineAnalysisResults 
          data={analysisResult}
          onNewAnalysis={handleNewAnalysis}
        />
      </Box>
    );
  }

  // Show form (default view)
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="h4"
          sx={{
            color: brandColors.primary,
            fontWeight: 600,
            mb: 1
          }}
        >
          🎬 Logline Analysis
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: brandColors.primary,
            opacity: 0.7
          }}
        >
          AI-powered story analysis with market insights and recommendations
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, maxWidth: 800, mx: 'auto' }}
          action={
            lastSubmission && (
              <Button
                color="inherit"
                size="small"
                startIcon={<RetryIcon />}
                onClick={handleRetry}
              >
                Retry
              </Button>
            )
          }
        >
          {error}
        </Alert>
      )}

      {/* Form */}
      <LoglineAnalysisForm 
        onSubmit={handleFormSubmit}
        loading={isLoading}
      />
    </Box>
  );
};

export default AgentsLoglineAnalysis;

